import type { LearningsStatus } from '@@/api/learnings/learnings-status';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { useWebsites } from '@/state/useWebsites';

const POLL_MS = 30_000;

/**
 * How far the learnings cron has got on the current website, for the Library's "writing
 * learnings" indicator. Asks nothing of a website that has learnings off.
 *
 * Polls only while something is pending, and when the last pending card lands it reloads the
 * Library's readings so the new cards appear without a manual refresh. That transition is
 * noticed inside the query function, against the value React Query already holds, rather
 * than in an effect watching the result.
 */
export const useLearningsStatus = () => {
  const apiClient = useClient();
  const queryClient = useQueryClient();
  const { website } = useWebsites();
  const websiteId = website?.id ?? '';
  const queryKey = ['LEARNINGS_STATUS', websiteId];

  const { data } = useQuery<LearningsStatus>({
    queryKey,
    enabled: !!websiteId && website?.learningsEnabled === true,
    queryFn: async () => {
      const before = queryClient.getQueryData<LearningsStatus>(queryKey);
      const status = await apiClient.get<LearningsStatus>(`/learnings/${websiteId}/status`);
      if (before && before.pending > 0 && status.pending === 0) {
        void queryClient.invalidateQueries({ queryKey: ['EXPERIMENTS', websiteId, 'readings'] });
      }
      return status;
    },
    refetchInterval: (query) => (query.state.data?.pending ? POLL_MS : false),
  });

  return {
    isGenerating: !!data && data.pending > 0,
    labelled: data?.labelled ?? 0,
    concluded: data?.concluded ?? 0,
    /** Why the cron is not writing for this website (its account's AI credits), or null. */
    paused: data?.paused ?? null,
  };
};
